<template>
  <section class="records-page">
    <DeleteDialog ref="deleteDialogRef" @onDelete="handleDeleteCall" />
    <CancelTranslateDialog
      :visible="cancelDialogVisible"
      :state="cancelDialogState"
      @confirm="onConfirmCancel"
      @keep="onKeepTranslating"
    />

    <header class="records-header">
      <div>
        <div class="records-title-row">
          <h1>{{ $t('account_to_route_title_2') }}</h1>
          <RetentionHelpTooltip />
        </div>
        <p>{{ $t('enterprise_ui_records_description') }}</p>
      </div>
      <span v-if="params.pageTotalSize" class="records-summary">{{ $t('enterprise_ui_page_summary', { page: params.pageNo, total: params.pageTotalSize }) }}</span>
    </header>

    <div id="fileUseList" class="records-table-shell">
      <div class="records-table-scroll">
        <div class="records-table-header records-table-grid">
          <div>{{ $t('account_enterprise_cost_record_table_header_name') }}</div>
          <div>{{ $t('account_enterprise_tranlsate_record_table_header_name') }}</div>
          <div>{{ $t('account_enterprise_table_header_alias') }}</div>
          <div>{{ $t('account_translate_recent_date') }}</div>
          <div>{{ $t('account_enterprise_tranlsate_record_table_header_lang') }}</div>
          <div>{{ $t('account_enterprise_tranlsate_record_table_header_stauts') }}</div>
          <div>{{ $t('account_enterprise_table_header_operation') }}</div>
        </div>

        <div v-if="tableData.length" class="records-table-body">
          <div v-for="(file, index) in tableData" :key="file.id" class="records-table-row records-table-grid">
            <div class="file-cell">
              <span class="file-type-icon" :style="{ backgroundColor: getRecordFileType(file).color }">
                <WorkbenchIcon :name="getRecordFileType(file).icon" :size="14" />
              </span>
              <el-popover
                placement="bottom-start"
                :width="320"
                trigger="hover"
                :show-after="120"
                :offset="6"
                popper-class="records-detail-popover"
                @show="onDetailShow(file)"
                @hide="onDetailHide(file)"
              >
                <template #reference>
                  <span class="file-name" :class="{ 'is-active': activeDetailId === file.id }">{{ file.origFileName }}</span>
                </template>
                <div class="record-detail">
                  <div class="record-detail-title">{{ $t('record_detail_title') }}</div>
                  <div class="record-detail-row">
                    <span class="record-detail-label">{{ $t('record_detail_doc_chars') }}</span>
                    <span class="record-detail-value">{{ detailDocChars(file) }}</span>
                  </div>
                  <div class="record-detail-row">
                    <span class="record-detail-label">{{ $t('record_detail_cost_chars') }}</span>
                    <span class="record-detail-value">{{ detailCostChars(file) }}</span>
                  </div>
                  <div v-if="showEngineDetail(file)" class="record-detail-row">
                    <span class="record-detail-label">{{ $t('record_detail_cost_ratio') }}</span>
                    <span class="record-detail-value">{{ detailCostRatio(file) }}</span>
                  </div>
                  <div class="record-detail-divider"></div>
                  <div class="record-detail-row">
                    <span class="record-detail-label">{{ $t('record_detail_image_translate') }}</span>
                    <span class="record-detail-value">{{ file.ocrFlag == 1 ? $t('record_detail_on_ocr') : $t('record_detail_off') }}</span>
                  </div>
                  <div v-if="showEngineDetail(file)" class="record-detail-row">
                    <span class="record-detail-label">{{ $t('record_detail_engine') }}</span>
                    <span class="record-detail-value">{{ file.transEngineIdName || '—' }}</span>
                  </div>
                  <div class="record-detail-row">
                    <span class="record-detail-label">{{ $t('record_detail_glossary') }}</span>
                    <span class="record-detail-value">{{ isGlossaryUsed(file) ? $t('record_detail_on') : $t('record_detail_off') }}</span>
                  </div>
                  <div v-if="isGlossaryUsed(file)" class="record-detail-row">
                    <span class="record-detail-label">{{ $t('record_detail_glossary_applied') }}</span>
                    <span class="record-detail-value">{{ detailGlossaryNames(file) }}</span>
                  </div>
                </div>
              </el-popover>
            </div>

            <div class="text-cell strong-cell">{{ file.bizMemberName || '—' }}</div>
            <div class="text-cell muted-cell">{{ file.bizMemberAlias || '—' }}</div>
            <div class="text-cell muted-cell">{{ formatDate(file.createTime) }}</div>

            <div class="language-cell">
              <span>{{ sourceLanguage(file) }}</span>
              <WorkbenchIcon name="arrow-left-right" :size="13" />
              <span>{{ targetLanguage(file) }}</span>
            </div>

            <div>
              <span class="record-status" :class="statusClass(file)">{{ getTranslateStatus(file) }}</span>
            </div>

            <div class="operation-cell">
              <button
                v-if="getPermission(file, EnterpriseMemberPermission.FILE_LOOK)"
                class="operation-button"
                type="button"
                @click="handleOperation(file, 'look')"
              >
                {{ $t('account_enterprise_permission_look') }}
              </button>

              <el-popover
                v-if="getPermission(file, EnterpriseMemberPermission.FILE_DOWNLOAD) && getShowCompareFile(file)"
                ref="popoverRef"
                width="auto"
                trigger="hover"
                popper-class="custom-chatPopover"
              >
                <template #reference>
                  <button class="operation-button" type="button">{{ $t('account_enterprise_permission_download') }}</button>
                </template>
                <div class="download-menu">
                  <button type="button" @click="onDownloadFile(file); closePopover(index)">{{ downloadFileText(file) }}</button>
                  <button
                    v-if="!(file.transReqJson && file.transReqJson.arxivId)"
                    type="button"
                    @click="onDownloadCompareFile(file); closePopover(index)"
                  >
                    {{ $t('download_file_btn2') }}
                  </button>
                </div>
              </el-popover>

              <button
                v-else-if="getPermission(file, EnterpriseMemberPermission.FILE_DOWNLOAD)"
                class="operation-button"
                type="button"
                @click="handleOperation(file, 'download')"
              >
                {{ $t('account_enterprise_permission_download') }}
              </button>

              <button
                v-if="canShowCancel(file)"
                class="operation-button"
                type="button"
                @click="handleOperation(file, 'cancel')"
              >
                {{ $t('cancel_translate_btn') }}
              </button>

              <button
                v-if="file.userId == userInfo.id || userInfo.bizTeamMemberRole == 1"
                class="operation-button is-danger"
                type="button"
                @click="handleOperation(file, 'delete')"
              >
                {{ $t('account_enterprise_operation_delete') }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="records-empty-state">
          <span class="records-empty-icon"><WorkbenchIcon name="file-clock" :size="28" /></span>
          <h2>{{ $t('no_data') }}</h2>
          <p>{{ $t('enterprise_ui_records_empty') }}</p>
        </div>
      </div>
    </div>

    <footer v-if="tableData.length" class="records-footer">
      <span>{{ $t('enterprise_ui_records_page_count', { count: tableData.length }) }}</span>
      <el-pagination
        v-model:current-page="params.pageNo"
        :page-size="params.pageSize"
        :background="false"
        layout="prev, pager, next"
        :page-count="params.pageTotalSize"
        :pager-count="getPagerCount"
        @current-change="handleCurrentChange"
      />
    </footer>
  </section>
</template>

<script setup lang="ts">
import DeleteDialog from '../Dialog/DeleteDialog.vue'
import CancelTranslateDialog from '../Dialog/CancelTranslateDialog.vue'
import RetentionHelpTooltip from './RetentionHelpTooltip.vue'
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import AccountServer from '~/services/AccountServer'
import { PermissionUtils, type FileItem, EnterpriseMemberPermission } from '~/services/AccountServer/types'
import EventBus from 'vue3-eventbus'
import { NetCode } from '~/types/const'
import useUserStore from '~/store/modules/user'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from "vue-router";
import { getReadModeArtifactKey, isReadModeRecord } from '@/utils/enterpriseTranslate'
import { canAccessEnterpriseImage, isEnterpriseImageRecord } from '~/utils/enterpriseImage'
import { useEnterpriseImageNavigation } from '~/composables/useEnterpriseImageNavigation'
import { useCancelTranslate } from '~/composables/useCancelTranslate'
import { useCancelEligibility } from '~/composables/useCancelEligibility'
import { isExcelTranslationExtension } from '@/utils/translationFileType'
import Toast from '~/utils/toast'

const router = useRouter()
const openImageRecord = useEnterpriseImageNavigation()
const route = useRoute()
const { buildTranslatedDownloadName } = useDownloadFileName()
const tableData = ref<FileItem[]>([])
const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)
const popoverRef = ref([])
const popoverRef1 = ref([])
const currentTranslate = ref<FileItem | null>(null)
const { t, locale } = useI18n()
const { canCancel } = useCancelEligibility()
const {
  dialogVisible: cancelDialogVisible,
  dialogState: cancelDialogState,
  openCancelDialog,
  onConfirmCancel,
  onKeepTranslating,
} = useCancelTranslate({
  onCancelled: () => {
    requestFileList(false)
    getEnterpriseInfo()
  },
  onFinalState: (finalStatus) => {
    Toast(finalStatus === 'fail' ? t('cancel_toast_already_failed') : t('cancel_toast_already_done'))
    requestFileList(false)
  },
  onError: (message) => {
    Toast(message || t('translateFail'))
    requestFileList(false)
  },
})
const params = reactive({
  pageSize: 10,
  pageNo: 1,
  pageTotalSize: 0,
  recordIds: null as number[] | null,
  prevPageId: null as number | null,
  nextPageId: null as number | null
})

watch(userInfo, (newVal)=>{
  getEnterpriseInfo()
})

watch(route, (newVal, oldVal)=>{
  if(newVal.query.menu?.includes('records')){
    if (userInfo.value && userInfo.value.bizTeamId) {
      requestFileList()
    }
  }
})

const getPermission = (item: FileItem, permission: number) => {
  if (isEnterpriseImageRecord(item)) return canAccessEnterpriseImage(item, userInfo.value, permission === EnterpriseMemberPermission.FILE_LOOK ? 'view' : 'download')
  return item.operateStatus == 1 && (
    PermissionUtils.hasPermission(userInfo.value.bizMemberpermission || 0, permission)
    || item.userId == userInfo.value.id
    || userInfo.value.bizTeamMemberRole == 1
  )
}

// 是否显示「取消」按钮：后端 cancelTranslateEstimate 对越权（非任务所有者）单独拦截为 OPERATE_INVALID，
// 管理员（bizTeamMemberRole==1）也不能取消别人的任务，所以这里不能像删除按钮那样加 `|| userInfo.bizTeamMemberRole == 1`。
const canShowCancel = (file: FileItem) => {
  return canCancel(file) && file.userId == userInfo.value.id
}

const getShowCompareFile = (item) => {
  let showCompareFlag = item.remark && item.finishFlag == 1 && item.remark.compareFileBlobName ? true : false
  if (item.fileType != 'pdf') {
    showCompareFlag = false
  }
  return showCompareFlag
}

const downloadFileText = (item: any) => {
  if (item.remark.isEdited) {
    return t('download_file_btn1_1')
  }
  return t('download_file_btn1')
}

// 动态计算是否启用折叠（即返回 3）
const getPagerCount = computed(() => {
  return params.pageTotalSize > 3 ? 3 : params.pageTotalSize
})

const getTranslateStatus = (item: FileItem) => {
  if (item.operateStatus == 0) {
    return t("translateing") + getPercentage(item.translatedRate || "0")
  }
  if (item.operateStatus == 1) {
    return t("translateSuccessful")
  }
  if (item.operateStatus == 2) {
    return item.failReason == 4 ? t("cancel_record_status") : t("translateFail")
  }
  return ""
}

const statusClass = (item: FileItem) => {
  if (item.operateStatus === 0) return 'is-processing'
  if (item.operateStatus === 1) return 'is-success'
  return 'is-failed'
}

const sourceLanguage = (item: FileItem) => item.operateTag?.source || t('upload_auto_check_lang')
const targetLanguage = (item: FileItem) => item.operateTag?.target || '—'

// ---------- 文件名 hover「任务详情」浮层 ----------
// 当前展开浮层的记录 id，用于让文件名与虚线在展开期间保持品牌蓝
const activeDetailId = ref<number | null>(null)
// 记录 id -> 已应用术语库名称列表（null 表示请求失败）
const glossaryNameCache = reactive<Record<number, string[] | null>>({})
const glossaryLoading = reactive<Record<number, boolean>>({})

// 数值千分位按当前语言格式化；itb 不是合法 BCP47 标签，需要映射；阿拉伯语强制使用拉丁数字
const localeIsoMap: Record<string, string> = {
  zh: 'zh-CN', 'zh-TW': 'zh-TW', en: 'en-US', ja: 'ja-JP', ko: 'ko-KR', hi: 'hi-IN',
  de: 'de-DE', ar: 'ar-AE-u-nu-latn', fr: 'fr-FR', id: 'id-ID', itb: 'it-IT', uk: 'uk-UA',
}
const formatDetailNumber = (value: unknown) => {
  const num = Number(value)
  if (value === null || value === undefined || value === '' || !Number.isFinite(num)) return '—'
  try {
    return num.toLocaleString(localeIsoMap[String(locale.value)] || 'en-US')
  } catch {
    return num.toLocaleString('en-US')
  }
}

const isCancelledRecord = (item: FileItem) => item.operateStatus == 2 && item.failReason == 4
const isFailedRecord = (item: FileItem) => item.operateStatus == 2 && item.failReason != 4

// 新版图片翻译（imageFlag=1）没有消耗系数与翻译引擎，其余记录都要展示
const showEngineDetail = (item: FileItem) => !isEnterpriseImageRecord(item)

// 文档字符：已取消显示「—」，翻译中显示实时值
const detailDocChars = (item: FileItem) => isCancelledRecord(item) ? '—' : formatDetailNumber(item.wholeCharsNum)
// 消耗字符：失败任务记 0，已取消展示实扣值
const detailCostChars = (item: FileItem) => isFailedRecord(item) ? formatDetailNumber(0) : formatDetailNumber(item.costCharsNum ?? 0)
// 消耗系数：后端为空时按 x1 展示
const detailCostRatio = (item: FileItem) => {
  const ratio = String(item.transEngineCostRatio ?? '').trim()
  return `x${ratio || '1'}`
}

const isGlossaryUsed = (item: FileItem) => {
  if (Number(item.useTerm) === 1) return true
  const termIds = item.transReqJson?.termIds
  return typeof termIds === 'string' ? termIds.trim() !== '' : Array.isArray(termIds) && termIds.length > 0
}

const detailGlossaryNames = (item: FileItem) => {
  const names = glossaryNameCache[item.id]
  if (names === undefined) return glossaryLoading[item.id] ? '…' : '—'
  if (names === null || names.length === 0) return '—'
  return names.join(' / ')
}

// 浮层展开时才按需拉取术语库名称，同一记录只请求一次
const loadGlossaryNames = (item: FileItem) => {
  if (!isGlossaryUsed(item) || glossaryNameCache[item.id] !== undefined || glossaryLoading[item.id]) return
  glossaryLoading[item.id] = true
  AccountServer.getArtInfos({ operateRecordId: item.id }).then((res) => {
    const list = res.code == '1' && Array.isArray(res.list) ? res.list : []
    glossaryNameCache[item.id] = list
      .map((log: any) => String(log.termName || log.useTermName || '').trim())
      .filter(Boolean)
  }).catch(() => {
    glossaryNameCache[item.id] = null
  }).finally(() => {
    glossaryLoading[item.id] = false
  })
}

const onDetailShow = (item: FileItem) => {
  activeDetailId.value = item.id
  loadGlossaryNames(item)
}
const onDetailHide = (item: FileItem) => {
  if (activeDetailId.value === item.id) activeDetailId.value = null
}

const getRecordFileType = (item: FileItem) => {
  const extension = String(item.origFileName || item.fileType || '').split('.').pop()?.toLowerCase()
  if (extension === 'xlsx' || extension === 'xls') return { icon: 'table', color: '#2fb876' }
  if (extension === 'pptx' || extension === 'ppt') return { icon: 'presentation', color: '#ef4b56' }
  if (['png', 'jpg', 'jpeg', 'webp'].includes(extension || '')) return { icon: 'image', color: '#1687f8' }
  if (extension === 'pdf') return { icon: 'file-text', color: '#ef4b56' }
  if (extension === 'docx' || extension === 'doc') return { icon: 'file-text', color: '#4169e1' }
  return { icon: 'file-text', color: '#7a8699' }
}

const getPercentage = (percentage: string) => {
  let fixed = parseFloat(percentage)
  if (fixed > 0) {
    return fixed.toFixed(2) + "%"
  }
  return fixed + "%"
}

const handleCurrentChange = (changeValue) => {
  params.pageNo = changeValue;
  requestFileList(false, true)
  document.body.scrollTo({ top: 0, behavior: 'smooth' });
}

const normalizeRecordField = (value: unknown) => {
  if (!value || typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

// 获取翻译记录
function requestFileList(_isInit = false, _fromPageChange = false) {
  let loading;
  let listDom = document.getElementById("fileUseList")
  if (listDom) {
    loading = ElLoading.service({
        lock: true,
        target: listDom
    })
  }
  const param: Record<string, any> = {
    pageSize: 10,
    pageNo: params.pageNo,
    modeType: 0,
  }
  if (params.recordIds) {
    param.recordIds = params.recordIds
  }
  AccountServer.getRequestAccountUseFilePageList(param).then((res) => {
    if (res.code == "1") {
      let list = res.dataList
      if (list && list.length > 0) {
        list.forEach((item) => {
          item.operateTag = normalizeRecordField(item.operateTag) as Record<string, any>
          item.remark = normalizeRecordField(item.remark) as Record<string, any>
          item.transReqJson = normalizeRecordField(item.transReqJson) as Record<string, any>
        })
        params.pageTotalSize = res.totalPageCount ?? 0;
        tableData.value = list
      } else {
        tableData.value = [];
        params.pageTotalSize = 0
      }
    }
  }).catch((error) => {
    Toast(error.message);
  }).finally(() => {
    loading && loading.close()
  })
}

function getEnterpriseInfo(){
  // 被邀请
  if (userInfo.value && userInfo.value.bizTeamId) {
    requestFileList()
  }
}

const deleteDialogRef = ref<InstanceType<typeof DeleteDialog>>()
const handleDeleteEnterprise = (file: FileItem) => {
  deleteDialogRef.value?.open(0, file)
}

const handleDeleteCall = () => {
  requestFileList(false)
}

function handleOperation(file: FileItem, operation: string) {
  if (operation == 'delete') {
    // 删除文件
    handleDeleteEnterprise(file)
  }else if (operation == 'download') {
    // 下载
    onDownloadFile(file)
  }else if (operation == 'look') {
    // 查看
    onLookFile(file)
  }else if (operation == 'cancel') {
    // 取消翻译
    openCancelDialog(file.id, file.origFileName)
  }
}

function onLookFile(object: any) {
  if (openImageRecord(object)) return
  AccountServer.getAccountFileCompare({ operateRecordId: object.id }).then((result) => {
    if (result.code === "1") {
      let routerPath = '/home/PDFTranslate'
      // 获取当前页面的完整域名
      const origin = window.location.origin;
      // 获取当前路径
      const path = window.location.pathname;
      // 获取第一级路由
      const firstLevelRoute = path.split('/')[1]; // 分割路径并获取第一级路由
      // window.open(href, '_blank');

      if (isReadModeRecord(object)) {
        const resultBlobName = result.data.operateBlobName || object.blobFileName || ''
        const { href } = router.resolve({
          path: '/home/ReadMarkdown',
          query: {
            oldPDF: result.data.sourceBlobName,
            newPDF: resultBlobName,
            operateRecordId: object.id,
            queryKey: getReadModeArtifactKey(resultBlobName),
            filename: object.origFileName,
          },
        })
        const link = document.createElement('a')
        link.setAttribute('href', href)
        link.setAttribute('target', '_blank')
        link.style.display = 'none'
        link.click()
        return
      }

      if (object.fileType == 'docx') {
        routerPath = '/home/WordTranslate'
      } else if (isExcelTranslationExtension(object.fileType)) {
        routerPath = '/home/ExcelTranslate'
      } else if (object.fileType == 'pptx') {
        routerPath = '/home/PPTXTranslate'
      } else if (object.fileType == 'txt' || object.fileType == 'srt') {
        routerPath = '/home/TextTranslate'
      } else if (object.fileType == 'png' || object.fileType == 'jpg' || object.fileType == 'jpeg') {
        routerPath = '/home/ImageTranslate'
      }

      console.log('href===',routerPath)
      const { href } = router.resolve({
        path: routerPath,
        query: { oldPDF: `${result.data.sourceBlobName}`, newPDF: `${result.data.operateBlobName}`, operateRecordId: object.id },
      });
      
      var a = document.createElement('a');
      a.setAttribute('href', href);
      a.setAttribute('target', '_blank');
      a.style.display = 'none';
      a.click();
    }
  }).catch(() => {

  })
}

function onDownloadFile(object: any, isBatch?:boolean) {
  currentTranslate.value = object
  // const req = { operateRecordId: object.id }

  let origFileName = object.origFileName
  if (object.remark.targetLangCode) {
    origFileName = buildTranslatedDownloadName(object.origFileName, object.remark.targetLangCode, {
      variant: 'mono',
      convertImageExtToPdf: !isEnterpriseImageRecord(object),
    })
  }
  let url = `${useRuntimeConfig().public.downloadUrl}${object.blobFileName}?originName=${encodeURIComponent(origFileName)}`
  
  const link = document.createElement('a')
  link.href = url
  link.download = origFileName
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// 下载双语
function onDownloadCompareFile(object: any) {
  currentTranslate.value = object
  onDownloadCompareFileReal(object)
}

function onDownloadCompareFileReal(object: any) {
  let origFileName = object.origFileName
  if (object.remark.targetLangCode) {
    origFileName = buildTranslatedDownloadName(object.origFileName, object.remark.targetLangCode, {
      variant: 'dual',
      convertImageExtToPdf: true,
    })
  }
  let url = `${useRuntimeConfig().public.downloadUrl}${object.remark.compareFileBlobName}?originName=${encodeURIComponent(origFileName)}`
  console.log('url===>>>', url)
  const link = document.createElement('a');
  link.href = url
  link.download = origFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const closePopover = (index: number) => {
  if (popoverRef.value[index] && typeof popoverRef.value[index].hide === 'function') {
    popoverRef.value[index].hide();
  }
};
const closePopover1 = (index: number) => {
  if (popoverRef1.value[index] && typeof popoverRef1.value[index].hide === 'function') {
    popoverRef1.value[index].hide();
  }
};

onUnmounted(()=>{
  EventBus.off('createAccountEnterprise', getEnterpriseInfo)
})

onMounted(()=>{
  getEnterpriseInfo()
  EventBus.on('createAccountEnterprise', getEnterpriseInfo)
})

</script>

<style lang="scss" scoped>
.enterprise-page {
  max-width: 100%;
}

.page-header {
  
}

.enterprise-add-btn {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  .add-btn-text {
    font-size: 12px;
    font-weight: 400;
    color: white;
    cursor: pointer;
    padding: 8px 20px;
    background-color: #2590E1;
    border-radius: 4px;
    &:hover {
      opacity: 0.8;
    }
  }
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
}

.enterprise-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid #BFBFBF;
  margin-top: 40px;
  .table-header {
    display: grid;
    grid-template-columns: 20% 15% 10% 15% 15% 15% 10%;
    padding: 16px 20px;
    border-bottom: 1px solid #BFBFBF;
    font-weight: 400;
    font-size: 12px;
    color: #262626;
    background-color: #EAF5FC;
  }

  .table-body {
    // max-height: 400px;
    // overflow-y: auto;
  }

  .table-not-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 253px;
    .icon-icon_blank1 {
      font-size: 48px;
      color: #BFBFBF;
    }
    .no-data-title {
      margin-top: 10px;
      font-size: 12px;
      font-weight: 400;
      color: #BFBFBF;
    }
    .no-data-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      width: 130px;
      height: 30px;
      background: #2590E1;
      border-radius: 4px 4px 4px 4px;
      margin-top: 30px;
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
      .icon-icon_add_1 {
        font-size: 16px;
        color: #fff;
      }
      .no-data-text {
        font-size: 12px;
        color: #fff;
        margin-left: 6px;
      }
    }
  }

  .table-row {
    display: grid;
    grid-template-columns: 20% 15% 10% 15% 15% 15% 10%;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
    align-items: center;
  }

  .table-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #262626;
    font-weight: 400;
  }

  .header-cell {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #262626;
  }

  .checkbox {
    justify-content: center;
  }

  .name {
    display: flex;
    flex-direction: row;
    align-items: center;
    .name-icon {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .file-name {
      font-size: 12px;
      color: #262626;
      font-weight: 400;
      max-width: 80%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .permission {
    display: flex;
    flex-direction: column;
    align-items: start;
    .permissionItems {
      display: flex;
      flex-direction: row;
 
      .permissionItem {
        display: flex;
        flex-direction: row;
        align-items: center;
        .permission-text {
          min-width: 40px;
        }
        .icon-icon_correct {
          color: #34A853;
          font-size: 14px;
          margin-left: 14px;
          margin-right: 6px;
        }
      }
    }
  }

  .operation {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    .operation-btn {
      display: flex;
      flex-direction: row;
      align-items: center;
      cursor: pointer;
      border-bottom: 0.5px solid transparent;
      &:hover {
        opacity: 0.8;
        border-bottom: 0.5px solid #2590E1;
      }
      .operation-btn-text {
        font-size: 12px;
        color: #2590E1;
        font-weight: 400;
      }
    } 
    .operation-btn-sub-title {
      font-size: 12px;
      color: #2590E1;
      font-weight: 400;
      border-bottom: 0.5px solid transparent;
      &:hover {
        opacity: 0.8;
        border-bottom: 0.5px solid #2590E1;
      }
    }
  }
  .status-badge {
    padding: 4px 0px;
    border-radius: 12px;
    font-size: 12px;
    color: #fff;
    
    &.status-translating {
      color: #F09308;
    }
    
    &.status-success {
      color: #595959;
    }
    
    &.status-failed {
      color: #595959;
    }
  }

  .creator, .date {
    font-size: 14px;
    color: #666;
  }
}
.chatDiv {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 20px;
  cursor: pointer;
  &:hover .icon-icon_onlineservice1 {
    color: #2590E1
  }
  &:hover .icon-icon_edit1 {
    color: #2590E1
  }
  &:hover .chat-title {
    color: #2590E1
  }
  .chat-title {
    font-size: 14px;
    color: #262626;
    font-weight: bold;
  }
}
.pageNation {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
.invited-enterprise-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  .enterprise-content-invited {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    .invited-icon {
      width: 80px;
      height: 80px;
      background: #2590E1;
      border-radius: 0px 0px 0px 0px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 80px;
      .icon-icon_enterprise {
        font-size: 48px;
        color: #fff;
      }
    }
    .invited-title {
      font-size: 16px;
      font-weight: 500;
      color: #262626;
      max-width: 256px;
      text-align: center;
      margin-top: 10px;
    }
    .invited-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      width: 100%;
      .invited-content-btn {
        width: 256px;
        height: 40px;
        background: #2590E1;
        border-radius: 4px 4px 4px 4px;
        border: 1px solid #2590E1;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 40px;
        cursor: pointer;
        &:hover {
          opacity: 0.8;
        }
        .invited-content-btn-text {
          font-size: 14px;
          font-weight: 400;
          color: #fff;
        }
      }
    }
  }
}

</style>

<style lang="scss" scoped>
.records-page {
  --records-primary: #2b7de9;
  --records-ink: #1f2329;
  --records-sub: #51565d;
  --records-muted: #98a2b3;
  --records-line: #e8ebf0;
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 18px;
  color: var(--records-ink);
}

.records-header {
  display: flex;
  min-height: 34px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.records-header h1 {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  line-height: 25px;
}

.records-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.records-header p {
  margin: 3px 0 0;
  color: var(--records-muted);
  font-size: 11.5px;
  line-height: 17px;
}

.records-summary {
  padding-top: 4px;
  color: var(--records-muted);
  font-size: 11.5px;
}

.records-table-shell {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--records-line);
  border-radius: 10px;
  background: #fff;
}

.records-table-scroll {
  min-width: 100%;
  overflow-x: auto;
}

.records-table-grid {
  display: grid;
  grid-template-columns: minmax(210px, 1fr) 120px 100px 150px 170px 110px 180px;
  min-width: 1080px;
  align-items: center;
  gap: 10px;
}

.records-table-header {
  padding: 10px 16px;
  background: #f5f9ff;
  color: var(--records-sub);
  font-size: 12px;
  font-weight: 600;
}

.records-table-row {
  min-height: 58px;
  box-sizing: border-box;
  padding: 11px 16px;
  border-bottom: 1px solid #f2f5f9;
  transition: background-color .16s ease;
}

.records-table-row:last-child { border-bottom: 0; }
.records-table-row:hover { background: #fbfcfe; }

.file-cell,
.language-cell,
.operation-cell {
  display: flex;
  min-width: 0;
  align-items: center;
}

.file-cell { gap: 8px; }

.file-type-icon {
  display: inline-flex;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #fff;
}

.file-name,
.text-cell {
  overflow: hidden;
  font-size: 12.5px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-name,
.strong-cell { color: var(--records-ink); }

/* 文件名整段可悬停：虚线下划线 dash 4 / gap 3 / 1px，宽度跟随文件名并在容器内截断 */
.file-name {
  display: inline-block;
  max-width: 100%;
  padding-bottom: 2px;
  font-weight: 600;
  cursor: pointer;
  background-image: repeating-linear-gradient(to right, #c9ced6 0 4px, transparent 4px 7px);
  background-repeat: no-repeat;
  background-size: 100% 1px;
  background-position: left bottom;
  transition: color 0.15s ease;
}

.file-name:hover,
.file-name.is-active {
  color: var(--records-primary);
  background-image: repeating-linear-gradient(to right, var(--records-primary) 0 4px, transparent 4px 7px);
}
.muted-cell { color: var(--records-muted); }

.language-cell {
  gap: 7px;
  color: var(--records-sub);
  font-size: 12px;
}

.language-cell .workbench-icon { color: var(--records-muted); }

.record-status {
  font-size: 12.5px;
  font-weight: 600;
}

.record-status.is-processing { color: #f09308; }
.record-status.is-success { color: #22a06b; }
.record-status.is-failed { color: #e5484d; }

.operation-cell {
  flex-wrap: wrap;
  gap: 10px;
}

.operation-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--records-primary);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.operation-button.is-danger { color: #e5484d; }
.operation-button:hover { text-decoration: underline; }

.records-empty-state {
  display: flex;
  min-height: 300px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 32px;
  text-align: center;
}

.records-empty-icon {
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #f2f6fb;
  color: #aab3c1;
}

.records-empty-state h2 {
  margin: 12px 0 0;
  color: var(--records-sub);
  font-size: 13px;
  font-weight: 600;
}

.records-empty-state p {
  margin: 6px 0 0;
  color: var(--records-muted);
  font-size: 12px;
}

.records-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--records-muted);
  font-size: 11.5px;
}

.records-footer :deep(.el-pagination) {
  --el-pagination-button-width: 26px;
  --el-pagination-button-height: 26px;
  --el-pagination-button-disabled-bg-color: #fff;
  --el-pagination-bg-color: #fff;
  --el-pagination-hover-color: var(--records-primary);
  padding: 0;
}

.records-footer :deep(.btn-prev),
.records-footer :deep(.btn-next),
.records-footer :deep(.number) {
  min-width: 26px;
  border: 1px solid #e3e7ec;
  border-radius: 6px;
}

.download-menu {
  display: flex;
  min-width: 120px;
  flex-direction: column;
  gap: 4px;
}

.download-menu button {
  padding: 7px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--records-sub);
  font: inherit;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
}

.download-menu button:hover {
  background: #f5f9ff;
  color: var(--records-primary);
}
</style>

<style lang="scss">
/* 翻译记录「任务详情」浮层：锚定文件名左下，圆角 8，同款描边与阴影 */
.el-popover.el-popper.records-detail-popover {
  --records-primary: #2b7de9;
  padding: 10px 14px;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  min-width: 0;

  .el-popper__arrow { display: none; }

  .record-detail {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .record-detail-title {
    font-size: 12px;
    font-weight: 600;
    line-height: normal;
    color: #1f2329;
    white-space: nowrap;
  }

  .record-detail-row {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .record-detail-label {
    flex: 0 0 120px;
    font-size: 12px;
    line-height: 17px;
    color: #8f959e;
  }

  .record-detail-value {
    flex: 1 1 0;
    min-width: 0;
    font-size: 12px;
    line-height: 17px;
    font-weight: 600;
    color: #51565d;
    word-break: break-word;
  }

  .record-detail-divider {
    height: 1px;
    background: #ebedf0;
  }
}
</style>
